import * as React from 'react';
import { Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import { compose } from 'redux';

import type { LaborUtilizationChartsViewModel } from 'ab-viewModels/laborUtilization.viewModel';

import { moneyNormalizer, prettyMoney } from '@acceligentllc/shared/utils/formatting';
import { normalizePercentages } from '@acceligentllc/shared/utils/number';

import { ColorPalette } from '@acceligentllc/shared/enums/color';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import { MONEY_FORMATTER } from 'af-utils/chart.util';

import BarMeter from 'af-components/BarMeter';

import * as EmployeeUtilizationActions from 'af-actions/employeeUtilization';

import InfoCardChart from '../../UtilizationShared/InfoCardChart';

interface OwnProps {
	daysAssigned: number;
	daysAvailable: number;
	daysUnavailable: number;
	totalDays: number;
	totalRevenue: number;
	targetProfit: number;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const InfoCard: React.FC<Props> = (props) => {
	const {
		fetchEmployeeUtilizationCharts,
		daysAssigned,
		daysAvailable,
		daysUnavailable,
		totalDays,
		totalRevenue,
		targetProfit,
	} = props;

	const [averageDailyRevenueChart, setAverageDailyRevenueChart] = React.useState<Nullable<LaborUtilizationChartsViewModel['averageDailyRevenueChart']>>(null);

	// On mount
	React.useEffect(() => {
		const initialize = async () => {
			const data = await fetchEmployeeUtilizationCharts();
			setAverageDailyRevenueChart(data?.averageDailyRevenueChart);
		};
		initialize();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const entries = [
		{ label: 'Scheduled', value: daysAssigned, color: ColorPalette.GREEN },
		{ label: 'Not Scheduled', value: daysAvailable, color: ColorPalette.ORANGE },
		{ label: 'Not Available', value: daysUnavailable, color: ColorPalette.RED },
	];

	const [scheduledPercentage, notScheduledPercentage, notAvailablePercentage] = normalizePercentages([
		totalDays ? Math.round(100 * daysAssigned / totalDays) : 0,
		totalDays ? Math.round(100 * daysAvailable / totalDays) : 0,
		totalDays ? Math.round(100 * daysUnavailable / totalDays) : 0,
	]);

	return (
		<div className={`utilization-info-card ${bemBlock('form-box', ['padded-inv'])}`}>
			<Row>
				<Col lg={8} sm={12} xs={24}>
					<span className={bemElement('utilization-info-card', 'labor-title')}>Labor</span>
					<BarMeter entries={entries} total={totalDays} />
					<div className={bemElement('utilization-info-card', 'group')}>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-right'])}>
							<span className="group-title">Scheduled:</span>
							<span className="group-value text-green">{scheduledPercentage}%</span>
						</div>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-right'])}>
							<span className="group-title">Not Scheduled:</span>
							<span className="group-value text-orange">{notScheduledPercentage}%</span>
						</div>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-right'])}>
							<span className="group-title">Not Available:</span>
							<span className="group-value text-red">{notAvailablePercentage}%</span>
						</div>
						<div className={bemElement('utilization-info-card', 'group-entry', ['padded-verticaly'])}>
							<span className="group-title">Total Revenue:{/* TODO: update label once costs are added to profit calculation */}</span>
							<span className="group-value">{moneyNormalizer(targetProfit)}</span>
						</div>
					</div>
				</Col>
				<Col className={bemElement('utilization-info-card', 'charts-row')} lg={16} sm={12} xs={24}>
					<div>
						{/** Average hours per month */}
					</div>
					<div>
						{/** Average MHA per month */}
					</div>
					<InfoCardChart
						chartData={averageDailyRevenueChart}
						footerLabel="Total Revenue:"
						footerValue={moneyNormalizer(totalRevenue)}
						hAxisFormatter={MONEY_FORMATTER}
						hAxisLabel="Month"
						title="Average Daily Revenue"
						vAxisFormatter={prettyMoney}
					/>
				</Col>
			</Row>
		</div>
	);
};

function mapDispatchToProps() {
	return {
		fetchEmployeeUtilizationCharts: EmployeeUtilizationActions.fetchEmployeeUtilizationCharts,
	};
}

const connector = connect(null, mapDispatchToProps());

const enhance = compose<React.ComponentType<OwnProps>>(
	React.memo,
	connector
);

export default enhance(InfoCard);
